import { useEffect, useMemo, useState } from 'react'
import { createDebitRequest, getServicingParticipation, updateDebitRequest } from 'services'
import { Modal } from 'stories/components'

import {
  type DebitPayer,
  type DebitPurpose,
  type DebitRequestRow,
  type LoanParticipantOption,
  PAYER_LABELS,
  PURPOSE_LABELS,
  PURPOSES_FOR_PAYER,
  SEC_CODE_OPTIONS,
} from './types'

/**
 * Enter or edit a Debit Request: who the money comes from, what for, how much, and optionally the
 * one-time banking they authorized for it.
 *
 * Two things shape this form.
 *
 * The payer decides where the banking comes from when none is typed here: a borrower falls through
 * to the loan's ACH record, an investor to their participant record. Leaving the banking section
 * blank is therefore the normal case for a serviced loan, and the form says so rather than making
 * a servicer re-key an account already on file.
 *
 * The API never returns an account number in full - only the last four digits - so an edit cannot
 * pre-fill what is stored. Editing a request that carries its own banking shows it as a masked
 * summary with a "Replace" button, and only a deliberate replace sends banking at all. Pre-filling
 * a masked value would send the mask back, and the backend refuses a masked number precisely
 * because that is how an account silently becomes undebitable.
 */
export const DebitRequestModal = ({
  editing,
  onClose,
}: {
  editing: DebitRequestRow | null
  onClose: (changed: boolean) => void
}) => {
  const [payer, setPayer] = useState<DebitPayer>(editing?.payer || 'borrower')
  const [purpose, setPurpose] = useState<DebitPurpose>(editing?.purpose || 'downpayment')
  const [participantUserId, setParticipantUserId] = useState(
    editing?.participantUserId ? String(editing.participantUserId) : '',
  )
  const [payerName, setPayerName] = useState(editing?.payerName || '')
  const [amount, setAmount] = useState(editing ? String(editing.amount) : '')
  const [dueDate, setDueDate] = useState(editing?.dueDate || '')
  const [memo, setMemo] = useState(editing?.memo || '')
  const [postToTrustLedger, setPostToTrustLedger] = useState(editing ? editing.postToTrustLedger : true)

  // Banking. `replacing` is what decides whether any banking is sent: on a new request the section
  // is simply open, and on an edit it stays shut until the servicer asks to replace what is stored.
  const [replacing, setReplacing] = useState(!editing)
  const [routingNumber, setRoutingNumber] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [accountType, setAccountType] = useState(editing?.accountType || 'checking')
  const [bankName, setBankName] = useState(editing?.bankName || '')
  const [secCode, setSecCode] = useState(editing?.secCode || '')

  const [participants, setParticipants] = useState<LoanParticipantOption[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // The loan's investors, from the same endpoint Servicing > Investors lists them with, so the
  // picker offers exactly the parties who hold a slice of this loan. `participantUserId` comes
  // back as a string there, hence the Number() before it is sent.
  useEffect(() => {
    getServicingParticipation()
      .then((res: any) => {
        const rows: LoanParticipantOption[] = (res?.participants || [])
          .map((p: any) => ({
            participantUserId: Number(p.participantUserId),
            participantName: String(p.participantName || ''),
          }))
          .filter((p: LoanParticipantOption) => p.participantUserId > 0)
        setParticipants(rows)
      })
      .catch(() => setParticipants([]))
  }, [])

  const purposes = useMemo(() => PURPOSES_FOR_PAYER[payer], [payer])

  /** Switching payer re-points the purpose, so an investor request cannot stay a downpayment. */
  const onPayerChange = (next: DebitPayer) => {
    setPayer(next)
    if (!PURPOSES_FOR_PAYER[next].includes(purpose)) setPurpose(PURPOSES_FOR_PAYER[next][0])
    if (next === 'borrower') setParticipantUserId('')
  }

  /** Naming the investor also names the payer, unless the servicer has typed a name themselves. */
  const onParticipantChange = (next: string) => {
    setParticipantUserId(next)
    const chosen = participants.find((p) => String(p.participantUserId) === next)
    if (chosen && (!payerName || participants.some((p) => p.participantName === payerName)))
      setPayerName(chosen.participantName)
  }

  const onSubmit = async () => {
    setError('')
    const value = Number(String(amount).replace(/,/g, ''))
    if (!(value > 0)) {
      setError('Enter an amount greater than zero')
      return
    }
    if (payer === 'participant' && !Number(participantUserId)) {
      setError('Select the investor this debits')
      return
    }
    // Both numbers or neither: a routing number without an account is a half-filled form, and the
    // backend would fall through to the party's account - debiting somewhere nobody authorized.
    if (replacing && (routingNumber.trim() !== '' || accountNumber.trim() !== '')) {
      if (routingNumber.trim() === '' || accountNumber.trim() === '') {
        setError('A one-time authorization needs both a routing number and an account number')
        return
      }
    }

    const data: Record<string, any> = {
      purpose,
      amount: value,
      dueDate,
      payerName,
      memo,
      postToTrustLedger,
      accountType,
      bankName,
      secCode,
    }
    if (!editing) data.payer = payer
    if (payer === 'participant') data.participantUserId = Number(participantUserId)
    // Only a deliberate entry sends banking. An edit that leaves the section shut omits both
    // fields, which is what keeps the stored authorization intact.
    if (replacing && routingNumber.trim() && accountNumber.trim()) {
      data.routingNumber = routingNumber.trim()
      data.accountNumber = accountNumber.trim()
    }

    setLoading(true)
    try {
      const res: any = editing ? await updateDebitRequest(editing.id, data) : await createDebitRequest(data)
      if (!res?.success) {
        // Api itself toasts any response carrying both `success` and `message`, and every refusal
        // here carries one, so repeating it inline would show the same sentence twice. `error` is
        // for the checks above, which never reach the server.
        if (!res?.message) setError('Could not save the request')
        return
      }
      onClose(true)
    } catch (err: any) {
      setError(err?.message || 'Could not save the request')
    } finally {
      setLoading(false)
    }
  }

  const storedBanking = editing?.hasOwnBanking
    ? `${editing.bankName || 'Bank'} account ending ${editing.accountNumberLast4 || '****'}`
    : ''

  const fallbackNote =
    payer === 'participant'
      ? "Leave this blank to debit the investor's banking on their participant record."
      : "Leave this blank to debit the banking on the loan's ACH record."

  return (
    <Modal
      title={editing ? `Edit Debit Request #${editing.id}` : 'New Debit Request'}
      titleOkay="Save"
      loading={loading}
      onClose={() => onClose(false)}
      onOk={onSubmit}
      isOpen
    >
      <div className="text-gray-900 w-144 max-w-full mt-2 mb-3 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[13px] text-gray-500 mb-1">Payer</div>
            <select
              className="text-[14px] border rounded px-2 py-2 w-full"
              value={payer}
              // The payer decides which stored record the banking falls back to, and the backend
              // refuses to change it on an existing request, so it is fixed once saved.
              disabled={!!editing}
              onChange={(e) => onPayerChange(e.target.value as DebitPayer)}
            >
              {(Object.keys(PAYER_LABELS) as DebitPayer[]).map((key) => (
                <option key={key} value={key}>
                  {PAYER_LABELS[key]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-[13px] text-gray-500 mb-1">Purpose</div>
            <select
              className="text-[14px] border rounded px-2 py-2 w-full"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value as DebitPurpose)}
            >
              {purposes.map((key) => (
                <option key={key} value={key}>
                  {PURPOSE_LABELS[key]}
                </option>
              ))}
            </select>
          </div>
        </div>

        {payer === 'participant' && (
          <div>
            <div className="text-[13px] text-gray-500 mb-1">Investor</div>
            <select
              className="text-[14px] border rounded px-2 py-2 w-full"
              value={participantUserId}
              onChange={(e) => onParticipantChange(e.target.value)}
            >
              <option value="">Select the investor this debits</option>
              {participants.map((p) => (
                <option key={p.participantUserId} value={String(p.participantUserId)}>
                  {p.participantName}
                </option>
              ))}
            </select>
            {!participants.length && (
              <p className="text-xs text-amber-800 mt-1">
                This loan has no investors on Servicing &gt; Investors yet, so there is nobody to debit. Add the
                participation first.
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[13px] text-gray-500 mb-1">Amount ($)</div>
            <input
              type="number"
              step="0.01"
              className="text-[14px] border rounded px-2 py-2 w-full"
              value={amount}
              placeholder="25,000.00"
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div>
            <div className="text-[13px] text-gray-500 mb-1">Due date</div>
            <input
              type="date"
              className="text-[14px] border rounded px-2 py-2 w-full"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-1">When the money is wanted. The ACH effective date is chosen when
              the batch is drafted.</p>
          </div>
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">Name on the ACH entry</div>
          <input
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={payerName}
            placeholder={payer === 'participant' ? 'The investor as the bank should see them' : 'The borrower'}
            onChange={(e) => setPayerName(e.target.value)}
          />
        </div>

        <div className="border-t pt-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[13px] font-medium text-gray-700">One-time banking</span>
            {editing && !replacing && (
              <button className="text-xs text-shade-blue hover:underline" onClick={() => setReplacing(true)}>
                Replace
              </button>
            )}
            {editing && replacing && (
              <button
                className="text-xs text-gray-500 hover:underline"
                onClick={() => {
                  setReplacing(false)
                  setRoutingNumber('')
                  setAccountNumber('')
                }}
              >
                Keep what is stored
              </button>
            )}
          </div>

          {editing && !replacing ? (
            <p className="text-sm text-gray-600">
              {storedBanking ? (
                <>
                  Debits the {storedBanking}.{' '}
                  <span className="text-gray-500">
                    The account number is not shown and cannot be read back, so replacing it means entering it again in
                    full.
                  </span>
                </>
              ) : (
                <>No one-time authorization on this request. {fallbackNote}</>
              )}
            </p>
          ) : (
            <>
              <p className="text-xs text-gray-500 mb-2">{fallbackNote}</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[13px] text-gray-500 mb-1">Routing number</div>
                  <input
                    className="text-[14px] border rounded px-2 py-2 w-full"
                    value={routingNumber}
                    placeholder="9 digits"
                    onChange={(e) => setRoutingNumber(e.target.value)}
                  />
                </div>
                <div>
                  <div className="text-[13px] text-gray-500 mb-1">Account number</div>
                  <input
                    className="text-[14px] border rounded px-2 py-2 w-full"
                    value={accountNumber}
                    placeholder="In full"
                    onChange={(e) => setAccountNumber(e.target.value)}
                  />
                </div>
                <div>
                  <div className="text-[13px] text-gray-500 mb-1">Account type</div>
                  <select
                    className="text-[14px] border rounded px-2 py-2 w-full"
                    value={accountType}
                    onChange={(e) => setAccountType(e.target.value)}
                  >
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                  </select>
                </div>
                <div>
                  <div className="text-[13px] text-gray-500 mb-1">Bank name</div>
                  <input
                    className="text-[14px] border rounded px-2 py-2 w-full"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}

          <div className="mt-3 w-1/2">
            <div className="text-[13px] text-gray-500 mb-1">SEC code</div>
            <select
              className="text-[14px] border rounded px-2 py-2 w-full"
              value={secCode}
              onChange={(e) => setSecCode(e.target.value)}
            >
              <option value="">
                Default ({payer === 'participant' ? 'CCD, an entity' : 'PPD, a consumer'})
              </option>
              {SEC_CODE_OPTIONS.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">Memo</div>
          <textarea
            className="text-[14px] border rounded px-2 py-2 w-full"
            rows={2}
            value={memo}
            placeholder="What this collects, e.g. cash to close per the settlement statement"
            onChange={(e) => setMemo(e.target.value)}
          />
        </div>

        <label className="text-xs text-gray-600 flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={postToTrustLedger}
            onChange={(e) => setPostToTrustLedger(e.target.checked)}
          />
          Book a trust-ledger deposit when this settles
        </label>

        {error && <div className="text-sm text-red-700">{error}</div>}
      </div>
    </Modal>
  )
}
